import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMyRepStats } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const fmtDollars = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;

export default function MyRepStatsPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchMyRepStats();
        setData(r);
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't load your stats.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "32px 24px" }} data-testid="my-rep-stats-page">
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>FIELD SALES · MY STATS</div>
            <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 44, color: NAVY, margin: "4px 0 0" }}>
              HELLO, {(user?.name || user?.email || "").toUpperCase()}
            </h1>
            <p style={{ color: "#5b5e69", marginTop: 4 }}>Your rep slug: <code style={pillStyle}>{user?.rep_slug}</code></p>
          </div>
          <button onClick={logout} style={btnGhost} data-testid="my-rep-logout">SIGN OUT</button>
        </div>
        <div style={{ marginTop: 6, display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <Link to="/admin/change-password" style={{ color: NAVY, fontSize: 13, textDecoration: "underline" }} data-testid="my-rep-change-pw-link">
            Change my password
          </Link>
          <Link to={`/assessment?rep=${user?.rep_slug || ""}&presenter=1`} data-testid="my-rep-run-assessment"
                style={{ background: GOLD, color: NAVY, padding: "10px 18px", borderRadius: 10, fontWeight: 700, textDecoration: "none", letterSpacing: 1.5, fontSize: 14 }}>
            🎯 RUN HEALTH SCORE / LEAKAGE ASSESSMENT
          </Link>
        </div>

        {loading && <p style={{ color: "#5b5e69", marginTop: 24 }}>Loading…</p>}
        {error && <div style={errBox}>{error}</div>}

        {data && (
          <>
            <div style={kpiGrid}>
              <Kpi label="Today's Signups" value={data?.today?.signups ?? 0} />
              <Kpi label="Today's Paid" value={data?.today?.paid_signups ?? 0} />
              <Kpi label="Week MRR Added" value={fmtDollars(data?.week?.mrr_cents)} />
              <Kpi label="Month MRR Added" value={fmtDollars(data?.month?.mrr_cents)} />
              <Kpi label="Total Paid Signups" value={data?.totals?.paid_signups ?? 0} />
              <Kpi label="Commission Owed" value={fmtDollars(data?.totals?.commission_owed_cents)} highlight />
              <Kpi label="Commission Paid" value={fmtDollars(data?.totals?.commission_paid_cents)} />
            </div>

            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7ee", marginTop: 24, padding: 20 }}>
              <h3 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 24, margin: "0 0 12px" }}>YOUR RECENT SIGNUPS</h3>
              {(data?.contractors?.length || 0) === 0 ? (
                <p style={{ color: "#5b5e69" }}>No signups yet. Keep going!</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="my-signups-table">
                  <thead>
                    <tr>
                      <th style={th}>Business</th>
                      <th style={th}>Owner</th>
                      <th style={th}>City</th>
                      <th style={th}>Plan</th>
                      <th style={th}>CC on file</th>
                      <th style={th}>Signed up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.contractors || []).map((c) => (
                      <tr key={c.id} style={{ borderTop: "1px solid #e5e7ee" }}>
                        <td style={td}>{c.business_name || "—"}</td>
                        <td style={td}>{c.owner_name || "—"}</td>
                        <td style={td}>{c.city || "—"}</td>
                        <td style={td}><code style={pillStyle}>{c.plan || "starter"}</code></td>
                        <td style={td}>{c.stripe_cc_on_file ? <span style={{ color: "#1A7A4A", fontWeight: 700 }}>YES</span> : <span style={{ color: "#A32D2D", fontWeight: 700 }}>NO</span>}</td>
                        <td style={td}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight }) {
  return (
    <div style={{ background: highlight ? NAVY : "#fff", color: highlight ? GOLD : NAVY, border: "1px solid #e5e7ee", borderRadius: 14, padding: 18 }}>
      <div style={{ color: highlight ? GOLD : "#5b5e69", letterSpacing: 2, fontSize: 11, fontWeight: 700 }}>{label.toUpperCase()}</div>
      <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 38, letterSpacing: 1, marginTop: 4 }}>{value}</div>
    </div>
  );
}

const kpiGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginTop: 28 };
const th = { textAlign: "left", padding: "10px 12px", color: NAVY, fontSize: 12, letterSpacing: 1.5, fontWeight: 700, background: BG };
const td = { padding: "10px 12px", color: NAVY, fontSize: 13 };
const btnGhost = { background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, padding: "10px 18px", borderRadius: 10, fontWeight: 700, letterSpacing: 2, cursor: "pointer" };
const pillStyle = { background: BG, padding: "2px 8px", borderRadius: 6, fontFamily: "monospace", fontSize: 12, color: NAVY };
const errBox = { background: "#FDECEC", color: "#A32D2D", padding: "12px 16px", borderRadius: 10, marginTop: 16 };
